/**
 * Global error boundary for applications
 *
 * This component catches React errors and displays them using the
 * standardized error handling system with enhanced error reporting.
 */

import React, { type JSX } from "react";
import posthog from "posthog-js";
import { ErrorBoundary as BaseErrorBoundary } from "./error-boundary";
import { ErrorAlert } from "./error-alert";
import { handleFrontendError } from "@charphq/errors";

interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
}

export function GlobalErrorBoundary({ children }: GlobalErrorBoundaryProps): JSX.Element {
  return (
    <BaseErrorBoundary
      onError={(error, errorInfo) => {
        // Log error to console in development
        if (import.meta.env.DEV) {
          console.error("Global error boundary caught an error:", error, errorInfo);
        }

        // Report to PostHog Error Tracking. The posthog-js singleton is
        // initialised by each app's PostHogProvider; wrap so this is a safe
        // no-op when PostHog is not configured.
        try {
          posthog.captureException(error, {
            componentStack: errorInfo.componentStack,
          });
        } catch {
          // PostHog unavailable — swallow so the fallback UI still renders.
        }
      }}
      fallback={(error, _errorInfo) => {
        const standardizedError = handleFrontendError(error);
        return (
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="max-w-md w-full">
              <ErrorAlert
                error={standardizedError}
                onRetry={() => window.location.reload()}
                showDetails={import.meta.env.DEV}
              />
            </div>
          </div>
        );
      }}
    >
      {children}
    </BaseErrorBoundary>
  );
}
